import * as THREE from "three";
import { EventManager } from "./EventManager";
import { GraphicsSettingsManager } from "./config/graphics";

/**
 * Konfiguracja SkyBox
 */
export interface SkyBoxConfig {
  cubeMapSize: number;
  cloudSpeed: number;
  cloudDensity: number;
  cloudScale: number;
  skyColor: number;
  cloudColor: number;
  horizonColor: number;
  fogNear: number;
  fogFar: number;
  qualityMultipliers: {
    [quality: string]: {
      cloudDensity: number;
      cloudScale: number;
      fogDensity: number;
    };
  };
}

type CloudMotionData = {
  orbitRadius: number;
  orbitAngle: number;
  orbitSpeedFactor: number;
  spinSpeedFactor: number;
  baseScale: number;
};

/**
 * Klasa implementująca realistyczne niebo z chmurami
 */
export class SkyBox {
  private _graphicsSettingsHandler: EventListener | null = null;
  private scene: THREE.Scene;
  private config: SkyBoxConfig;
  private skyMesh!: THREE.Mesh;
  private cloudMaterials: THREE.MeshBasicMaterial[] = [];
  private cloudMeshes: THREE.Mesh[] = [];
  private cloudTextureLoader: THREE.TextureLoader;
  private cloudTextures: THREE.Texture[] = [];
  private cloudMotionData: CloudMotionData[] = [];
  private selectedCloudTextures: string[] = [];
  private readonly CLOUD_TEXTURE_PATH = "/assets/textures/clouds/";
  // Shared geometries for cloud rendering optimization
  private sharedCloudGeometries: THREE.PlaneGeometry[] = [];
  private readonly TOTAL_CLOUD_TEXTURES = 100;
  private readonly SELECTED_CLOUD_TEXTURES = 10;

  /**
   * Tworzy nowy SkyBox
   * @param scene Scena Three.js
   * @param config Konfiguracja skyboxa
   */
  constructor(scene: THREE.Scene, config: SkyBoxConfig) {
    this.scene = scene;
    this.config = config;
    this.cloudTextureLoader = new THREE.TextureLoader();

    console.log("[SkyBox] Constructor initialized with config:", {
      cloudDensity: config.cloudDensity,
      cloudScale: config.cloudScale,
      fogNear: config.fogNear,
      fogFar: config.fogFar,
    });

    // Create a handler function that we can reuse for both listeners
    const handleGraphicsSettingsChanged = ((event: CustomEvent) => {
      console.log("[SkyBox] Received graphics settings event:", event);
      try {
        if (!event.detail || typeof event.detail !== "object") {
          throw new Error("Invalid event detail structure");
        }

        const settings = event.detail.settings;
        if (!settings || typeof settings !== "object") {
          console.error("[SkyBox] Invalid settings object");
          return;
        }

        const qualityMultipliers = this.config.qualityMultipliers[settings.quality];
        if (!qualityMultipliers) {
          console.error("[SkyBox] Invalid quality multipliers for quality:", settings.quality);
          return;
        }

        // Ensure all settings properties exist with defaults if needed
        const fogEnabled = settings.fogEnabled !== undefined ? settings.fogEnabled : true;
        const fogDensity = settings.fogDensity !== undefined ? settings.fogDensity : 1.0;
        const cloudQuality = Math.max(0, settings.cloudQuality !== undefined ? settings.cloudQuality : 0);

        // Calculate multipliers
        const cloudDensityMultiplier = cloudQuality > 0 ? qualityMultipliers.cloudDensity : 0;
        const cloudScaleMultiplier = cloudQuality > 0 ? qualityMultipliers.cloudScale : 0;
        const fogDensityMultiplier = fogEnabled ? qualityMultipliers.fogDensity : 0;

        // Update config with calculated values
        this.config = {
          ...this.config,
          cloudDensity: cloudQuality > 0 ? this.config.cloudDensity * cloudDensityMultiplier : 0,
          cloudScale: cloudQuality > 0 ? this.config.cloudScale * cloudScaleMultiplier : 0,
          fogNear: fogEnabled ? this.config.fogNear * fogDensity : 1000000,
          fogFar: fogEnabled ? this.config.fogFar * fogDensity * fogDensityMultiplier + this.config.fogNear : 1000001,
        };

        // Update cloud meshes
        this.cloudMeshes.forEach((mesh, index) => {
          if (mesh.material instanceof THREE.MeshBasicMaterial) {
            mesh.material.opacity = cloudQuality > 0 ? Math.min(1.0, this.config.cloudDensity) : 0;
            mesh.visible = cloudQuality > 0;
          }
          if (cloudQuality > 0) {
            const motion = this.cloudMotionData[index];
            const scaledBase = (motion?.baseScale ?? 1) * this.config.cloudScale;
            mesh.scale.set(scaledBase, scaledBase, 1);
          }
        });

        console.log("[SkyBox] clouds quality:", cloudQuality);
        console.log("[SkyBox] fog quality:", fogDensity);
        console.log("[SkyBox] fog enabled:", fogEnabled);
        console.log("[SkyBox] fog near:", this.config.fogNear);
        console.log("[SkyBox] fog far:", this.config.fogFar);

        // Update fog
        if (this.scene.fog) {
          if (fogEnabled) {
            (this.scene.fog as THREE.Fog).near = this.config.fogNear;
            (this.scene.fog as THREE.Fog).far = this.config.fogFar;
            this.scene.fog.color.set(this.config.horizonColor);
          } else {
            (this.scene.fog as THREE.Fog).near = 1000000;
            (this.scene.fog as THREE.Fog).far = 1000001;
          }
        }

        this.updateConfig(this.config);
      } catch (error) {
        console.error("[SkyBox] Error updating graphics settings:", error);
      }
    }) as EventListener;

    // Use EventManager to register the event listener
    EventManager.addListener("SkyBox", document, "graphics:settingsChanged", handleGraphicsSettingsChanged);

    console.log("[SkyBox] Event listener for graphics:settingsChanged registered through EventManager");

    // Store the handler for cleanup
    this._graphicsSettingsHandler = handleGraphicsSettingsChanged;

    // Apply current settings immediately without dispatching an event
    // This prevents duplicate events when initializing components
    const currentSettings = GraphicsSettingsManager.getInstance().getCurrentSettings();
    console.log("[SkyBox] Applying current settings on initialization:", currentSettings);

    // Apply settings directly to our internal config
    try {
      const qualityMultipliers = this.config.qualityMultipliers[currentSettings.quality];
      if (qualityMultipliers) {
        // Ensure all settings properties exist with defaults if needed
        const fogEnabled = currentSettings.fogEnabled !== undefined ? currentSettings.fogEnabled : true;
        const fogDensity = currentSettings.fogDensity !== undefined ? currentSettings.fogDensity : 1.0;
        const cloudQuality = Math.max(0, currentSettings.cloudQuality !== undefined ? currentSettings.cloudQuality : 0);

        // Calculate multipliers
        const cloudDensityMultiplier = cloudQuality > 0 ? qualityMultipliers.cloudDensity : 0;
        const cloudScaleMultiplier = cloudQuality > 0 ? qualityMultipliers.cloudScale : 0;
        const fogDensityMultiplier = fogEnabled ? qualityMultipliers.fogDensity : 0;

        // Update config with calculated values
        this.config = {
          ...this.config,
          cloudDensity: cloudQuality > 0 ? this.config.cloudDensity * cloudDensityMultiplier : 0,
          cloudScale: cloudQuality > 0 ? this.config.cloudScale * cloudScaleMultiplier : 0,
          fogNear: fogEnabled ? this.config.fogNear * fogDensity : 1000000,
          fogFar: fogEnabled ? this.config.fogFar * fogDensity * fogDensityMultiplier + this.config.fogNear : 1000001,
        };
      }
    } catch (error) {
      console.error("[SkyBox] Error applying initial settings:", error);
    }

    // Select random cloud textures and create scene elements
    this.selectRandomCloudTextures();
    this.createSky();
    this.setupFog();
    this.createClouds();
  }

  /**
   * Wybiera losowo 10 tekstur chmur z dostępnych 100
   */
  private selectRandomCloudTextures(): void {
    // Tworzymy tablicę z nazwami wszystkich dostępnych tekstur
    const availableTextures: string[] = [];
    for (let i = 1; i <= this.TOTAL_CLOUD_TEXTURES; i++) {
      // Formatujemy nazwę pliku z wiodącymi zerami (01.jpg, 02.jpg, itd.)
      const fileName = i < 10 ? `0${i}.png` : `${i}.png`;
      availableTextures.push(fileName);
    }

    // Mieszamy tablicę
    this.shuffleArray(availableTextures);

    // Wybieramy pierwsze 10 elementów
    this.selectedCloudTextures = availableTextures.slice(0, this.SELECTED_CLOUD_TEXTURES);
  }

  /**
   * Miesza elementy tablicy (algorytm Fisher-Yates)
   * @param array Tablica do pomieszania
   */
  private shuffleArray(array: any[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  /**
   * Aktualizuje konfigurację skyboxa
   * @param config Nowa konfiguracja
   */
  public updateConfig(config: SkyBoxConfig): void {
    this.config = config;

    // Aktualizacja koloru nieba
    if (this.skyMesh && this.skyMesh.material instanceof THREE.ShaderMaterial) {
      this.skyMesh.material.uniforms.topColor.value.set(this.config.skyColor);
      this.skyMesh.material.uniforms.bottomColor.value.set(this.config.horizonColor);
    }

    // Aktualizacja mgły
    if (this.scene.fog) {
      this.scene.fog.color.set(this.config.horizonColor);
      (this.scene.fog as THREE.Fog).near = this.config.fogNear;
      (this.scene.fog as THREE.Fog).far = this.config.fogFar;
    }
  }

  /**
   * Konfiguruje mgłę dla sceny
   */
  private setupFog(): void {
    // Tworzymy mgłę pasującą do koloru horyzontu

    // Upewniamy się, że wartości są liczbami
    const fogNear = Number(this.config.fogNear);
    const fogFar = Number(this.config.fogFar);

    // Sprawdzamy, czy wartości są poprawne
    if (isNaN(fogNear) || isNaN(fogFar)) {
      return;
    }

    // Usuwamy istniejącą mgłę, jeśli istnieje
    if (this.scene.fog) {
      //console.log('[SkyBox] Usuwanie istniejącej mgły');
    }

    // Tworzymy nową mgłę z konkretnymi wartościami
    this.scene.fog = new THREE.Fog(this.config.horizonColor, fogNear, fogFar);

    // Sprawdzamy, czy wartości zostały poprawnie ustawione
    const fog = this.scene.fog as THREE.Fog;
  }

  /**
   * Ładuje wybraną teksturę chmur
   * @param fileName Nazwa pliku tekstury
   * @returns Promise z załadowaną teksturą
   */
  private loadCloudTexture(fileName: string): Promise<THREE.Texture> {
    const fullPath = `${this.CLOUD_TEXTURE_PATH}${fileName}`;

    return new Promise((resolve, reject) => {
      this.cloudTextureLoader.load(
        fullPath,
        (texture) => {
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          // Ustawienia dla PNG z przezroczystością
          texture.premultiplyAlpha = true;
          texture.needsUpdate = true;
          resolve(texture);
        },
        (progressEvent) => {
          // Opcjonalny callback postępu
        },
        (error) => {
          console.error(`[SkyBox] Błąd ładowania tekstury chmur ${fileName}:`, error);
          reject(error);
        },
      );
    });
  }

  /**
   * Tworzy niebo z gradientem
   */
  private createSky(): void {
    // Ustawiamy kolor tła sceny na kolor nieba
    this.scene.background = new THREE.Color(this.config.skyColor);

    // Shader dla nieba z gradientem
    const vertexShader = `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform float offset;
      uniform float exponent;
      varying vec3 vWorldPosition;
      void main() {
        float h = normalize(vWorldPosition + offset).y;
        gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
      }
    `;

    // Materiał nieba
    const skyMaterial = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(this.config.skyColor) },
        bottomColor: { value: new THREE.Color(this.config.horizonColor) },
        offset: { value: 33 },
        exponent: { value: 0.6 },
      },
      vertexShader,
      fragmentShader,
      side: THREE.BackSide, // Odwraca powierzchnię do wewnątrz
      fog: false,
    });

    // Geometria nieba
    // Reduced segments for better performance (24,16 instead of 32,32)
    const skyGeometry = new THREE.SphereGeometry(350, 24, 16);
    this.skyMesh = new THREE.Mesh(skyGeometry, skyMaterial);
    // Perf: dome always surrounds the camera - skip per-frame culling tests.
    this.skyMesh.frustumCulled = false;
    this.scene.add(this.skyMesh);
  }

  /**
   * Tworzy chmury na niebie
   */
  private async createClouds(): Promise<void> {
    try {
      // Ładujemy wszystkie wybrane tekstury
      const texturePromises = this.selectedCloudTextures.map((fileName) => this.loadCloudTexture(fileName));

      this.cloudTextures = await Promise.all(texturePromises);
      this.cloudMotionData = [];

      // Create shared geometries for performance (reuse instead of creating new for each cloud)
      this.sharedCloudGeometries = [
        new THREE.PlaneGeometry(40, 40), // Small clouds
        new THREE.PlaneGeometry(55, 55), // Medium clouds (avg of 40-70)
        new THREE.PlaneGeometry(75, 75), // Large clouds (avg of 60-90)
      ];

      // Reuse one material per texture to reduce GPU state/material churn.
      this.cloudMaterials = this.cloudTextures.map(
        (texture) =>
          new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            opacity: Math.min(1.0, this.config.cloudDensity),
            depthWrite: false,
            side: THREE.DoubleSide,
            fog: false,
            alphaTest: 0.01,
          }),
      );

      const registerCloud = (
        geometry: THREE.PlaneGeometry,
        material: THREE.MeshBasicMaterial,
        heightBase: number,
        heightRange: number,
        radiusBase: number,
        radiusRange: number,
        scaleBase: number,
        scaleRange: number,
      ) => {
        const cloudMesh = new THREE.Mesh(geometry, material);
        const height = heightBase + Math.random() * heightRange;
        const radius = radiusBase + Math.random() * radiusRange;
        const angle = Math.random() * Math.PI * 2;
        const posX = Math.sin(angle) * radius;
        const posZ = Math.cos(angle) * radius;
        const baseScale = scaleBase + Math.random() * scaleRange;
        const scaledBase = baseScale * this.config.cloudScale;

        cloudMesh.position.set(posX, height, posZ);
        cloudMesh.rotation.x = -Math.PI / 2;
        cloudMesh.rotation.z = Math.random() * Math.PI * 2;
        cloudMesh.scale.set(scaledBase, scaledBase, 1);

        this.cloudMotionData.push({
          orbitRadius: radius,
          orbitAngle: angle,
          orbitSpeedFactor: 0.5 + Math.random() * 0.5,
          spinSpeedFactor: 0.05 + Math.random() * 0.08,
          baseScale,
        });

        this.cloudMeshes.push(cloudMesh);
        this.scene.add(cloudMesh);
      };

      // Base cloud set: one cloud per selected texture.
      for (let i = 0; i < this.cloudTextures.length; i++) {
        registerCloud(this.sharedCloudGeometries[0], this.cloudMaterials[i], 20, 40, 20, 60, 1.5, 1.0);
      }

      // Extra medium clouds.
      for (let i = 0; i < 15; i++) {
        const textureIndex = Math.floor(Math.random() * this.cloudMaterials.length);
        registerCloud(this.sharedCloudGeometries[1], this.cloudMaterials[textureIndex], 50, 50, 60, 100, 1.2, 0.8);
      }

      // Extra large horizon clouds.
      for (let i = 0; i < 15; i++) {
        const textureIndex = Math.floor(Math.random() * this.cloudMaterials.length);
        registerCloud(this.sharedCloudGeometries[2], this.cloudMaterials[textureIndex], 70, 50, 350, 100, 1.2, 0.8);
      }
    } catch (error) {
      console.error("[SkyBox] Błąd podczas tworzenia chmur:", error);
    }
  }

  /**
   * Aktualizuje pozycję chmur
   * @param deltaTime Czas od ostatniej klatki
   */
  /**
   * Cleanup method to remove event listeners and dispose resources
   */
  public dispose(): void {
    if (this._graphicsSettingsHandler) {
      EventManager.removeListener("SkyBox", document, "graphics:settingsChanged", this._graphicsSettingsHandler);
      this._graphicsSettingsHandler = null;
    }

    // Dispose cloud materials and textures
    this.cloudMaterials.forEach((material) => material.dispose());
    this.cloudTextures.forEach((texture) => texture.dispose());
    // Dispose shared geometries
    this.sharedCloudGeometries.forEach((geometry) => geometry.dispose());

    // Clear arrays
    this.cloudMaterials = [];
    this.cloudTextures = [];
    this.cloudMeshes = [];
    this.cloudMotionData = [];
    this.sharedCloudGeometries = [];
  }

  public update(deltaTime: number): void {
    // Aktualizacja pozycji chmur
    for (let i = 0; i < this.cloudMeshes.length; i++) {
      const cloud = this.cloudMeshes[i];
      if (!cloud.visible) {
        continue;
      }
      const motion = this.cloudMotionData[i];
      if (!motion) {
        continue;
      }

      const orbitSpeed = this.config.cloudSpeed * motion.orbitSpeedFactor;
      motion.orbitAngle += orbitSpeed * deltaTime;

      cloud.position.x = Math.cos(motion.orbitAngle) * motion.orbitRadius;
      cloud.position.z = Math.sin(motion.orbitAngle) * motion.orbitRadius;
      cloud.rotation.z += orbitSpeed * motion.spinSpeedFactor * deltaTime;
    }
  }
}
